import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Link,
  Collapse,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

export default function CookieConsent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieChoice = localStorage.getItem('cookieConsent');
    if (!cookieChoice) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);

    // Initialize analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);

    // Disable analytics if declined
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleLearnMore = () => {
    navigate('/cookie-policy');
  };

  return (
    <Collapse in={showBanner}>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: 2, md: 3 },
          backgroundColor: colors.offWhite,
          borderTop: `3px solid ${colors.red}`,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: colors.darkRed,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              {t('cookie.message', 'We use cookies to enhance your experience and analyze site usage.')}
              {' '}
              <Link
                component="button"
                onClick={handleLearnMore}
                sx={{
                  color: colors.red,
                  textDecoration: 'underline',
                  fontSize: 'inherit',
                }}
              >
                {t('cookie.learnMore', 'Learn more')}
              </Link>
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleDecline}
              sx={{
                color: colors.darkRed,
                borderColor: colors.darkRed,
                flex: { xs: 1, md: 'initial' },
                '&:hover': {
                  borderColor: colors.red,
                  backgroundColor: 'rgba(151, 0, 0, 0.04)',
                },
              }}
            >
              {t('cookie.decline', 'Decline')}
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              sx={{
                backgroundColor: colors.red,
                color: colors.yellow,
                flex: { xs: 1, md: 'initial' },
                '&:hover': {
                  backgroundColor: colors.darkRed,
                },
              }}
            >
              {t('cookie.accept', 'Accept')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
}
